import axios from 'axios';

const isLocalHost = () => {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1';
};

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  (isLocalHost() ? 'http://localhost:8000/api' : `${window.location.origin}/api`);

const STORAGE_BASE_URL =
  process.env.REACT_APP_STORAGE_URL ||
  (isLocalHost() ? 'http://localhost:8000/storage' : `${window.location.origin}/storage`);

const stripTrailingGarbage = (data) => {
  if (typeof data !== 'string' || data.length === 0) return data;
  const trimmed = data.trim();
  if (trimmed.charAt(0) !== '{' && trimmed.charAt(0) !== '[') return data;
  const openChar = trimmed.charAt(0);
  const closeChar = openChar === '{' ? '}' : ']';
  let depth = 0;
  let inString = false;
  let escaped = false;
  let endIdx = -1;
  for (let i = 0; i < trimmed.length; i++) {
    const c = trimmed.charAt(i);
    if (inString) {
      if (escaped) { escaped = false; }
      else if (c === '\\') { escaped = true; }
      else if (c === '"') { inString = false; }
    } else {
      if (c === '"') { inString = true; }
      else if (c === openChar) { depth++; }
      else if (c === closeChar) {
        depth--;
        if (depth === 0) { endIdx = i; break; }
      }
    }
  }
  if (endIdx > 0) {
    return trimmed.substring(0, endIdx + 1);
  }
  return data;
};

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Accept': 'application/json',
  },
  withCredentials: false,
  transformResponse: [
    (data) => {
      const cleaned = stripTrailingGarbage(data);
      try {
        return JSON.parse(cleaned);
      } catch (e) {
        return data;
      }
    },
  ],
});

const TOKEN_KEY = 'flare_token';
const USER_KEY = 'flare_user';

const readAnyToken = () => {
  try {
    return (
      window.localStorage.getItem(TOKEN_KEY) ??
      window.sessionStorage.getItem(TOKEN_KEY) ??
      null
    );
  } catch (e) {
    return null;
  }
};

const clearAllAuth = () => {
  try {
    window.localStorage.removeItem(TOKEN_KEY);
    window.localStorage.removeItem(USER_KEY);
  } catch (e) {}
  try {
    window.sessionStorage.removeItem(TOKEN_KEY);
    window.sessionStorage.removeItem(USER_KEY);
  } catch (e) {}
};

apiClient.interceptors.request.use(
  (config) => {
    if (config.skipAuth) {
      return config;
    }
    const token = readAnyToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.config?.skipAuth) {
      return Promise.reject(error);
    }
    if (error.response && error.response.status === 401) {
      clearAllAuth();
      if (
        !window.location.pathname.includes('/login') &&
        !window.location.pathname.includes('/portal-hd-99/login')
      ) {
        window.location.href = '/portal-hd-99/login';
      }
    }
    return Promise.reject(error);
  }
);

export const getApiBaseUrl = () => API_BASE_URL;
export const getStorageUrl = (path) => (path ? `${STORAGE_BASE_URL}/${path}` : null);

const api = {
  get: (url, config = {}) => apiClient.get(url, config),
  post: (url, data = {}, config = {}) => apiClient.post(url, data, config),
  put: (url, data = {}, config = {}) => apiClient.put(url, data, config),
  patch: (url, data = {}, config = {}) => apiClient.patch(url, data, config),
  delete: (url, config = {}) => apiClient.delete(url, config),
};

export default api;
